/**
 * Utilitaire d'impersonation de centre pour les super-admins
 * Stocke l'info dans sessionStorage (auto-nettoyé à la fermeture de l'onglet)
 * Communique les changements via un CustomEvent
 *
 * Sécurité :
 * - setImpersonation() valide côté serveur que le caller est super_admin
 * - getImpersonation() valide le format UUID du centerId
 * - Les données expirent après 8h
 */

import { supabase } from '@/lib/supabase'

const STORAGE_KEY = 'sa_impersonate_center'
export const IMPERSONATION_EVENT = 'impersonation-changed'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const EXPIRY_MS = 8 * 60 * 60 * 1000 // 8 hours

export interface ImpersonationData {
  centerId: string
  centerName: string
  // Champs optionnels pour impersonation utilisateur
  userId?: string
  userName?: string
  userEmail?: string
  userRole?: string
  // Timestamp de création (pour expiry)
  _ts?: number
}

export function getImpersonation(): ImpersonationData | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as ImpersonationData

    // Validate UUID format
    if (!data.centerId || !UUID_RE.test(data.centerId)) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }

    // Check expiry
    if (data._ts && Date.now() - data._ts > EXPIRY_MS) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }

    return data
  } catch {
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  }
}

/**
 * Set impersonation with backend validation.
 * Verifies the current user is super_admin before storing.
 * Returns false if validation fails.
 */
export async function setImpersonation(data: ImpersonationData): Promise<boolean> {
  // Verify caller is super_admin via Supabase
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) {
    console.error('[Impersonation] No active session')
    return false
  }

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', session.user.id)
    .single()

  if (error || profile?.role !== 'super_admin') {
    console.error('[Impersonation] Denied: caller is not super_admin')
    return false
  }

  // Validate centerId format
  if (!UUID_RE.test(data.centerId)) {
    console.error('[Impersonation] Invalid centerId format')
    return false
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, _ts: Date.now() }))
  window.dispatchEvent(new Event(IMPERSONATION_EVENT))
  return true
}

export function clearImpersonation(): void {
  sessionStorage.removeItem(STORAGE_KEY)
  window.dispatchEvent(new Event(IMPERSONATION_EVENT))
}

export function isImpersonating(): boolean {
  return sessionStorage.getItem(STORAGE_KEY) !== null
}
